import {
  type RoleRow,
} from "@nexys/user-management-client";
import { AlertCircle, Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";

export interface TenantRolesProps {
  organizationId: string;
  canManage?: boolean;
}

/** "invoices:read, reports:*" → ["invoices:read", "reports:*"] */
const parsePermissions = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);

const PermissionBadges = ({ permissions }: { permissions: string[] }) =>
  permissions.length === 0 ? (
    <span className="text-sm text-muted-foreground">{"—"}</span>
  ) : (
    <div className="flex max-w-md flex-wrap gap-1">
      {permissions.map((p) => (
        <Badge key={p} variant="secondary" className="font-mono text-xs">
          {p}
        </Badge>
      ))}
    </div>
  );

export const TenantRoles = ({
  organizationId,
  canManage = false,
}: TenantRolesProps) => {
  const client = useUmClient();
  const { data, error, loading, refresh } = useCall("listTenantRoles", {
    orgId: organizationId,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RoleRow | null>(null);

  const removeRole = async (role: RoleRow) => {
    setBusy(`del-${role.id}`);
    setActionError(null);
    const res = await client.deleteTenantRole({
      orgId: organizationId,
      roleId: role.id,
    });
    setBusy(null);
    if (res.ok) {
      await refresh();
    } else {
      setActionError(errorMessage(res.error, "Failed to delete role."));
    }
  };

  const roles = data?.roles ?? [];

  return (
    <Card>
      <CardHeader className="space-y-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-4 text-muted-foreground" />
              Roles
            </CardTitle>
            <CardDescription>
              {roles.length} custom role{roles.length === 1 ? "" : "s"} defined
              for this tenant. Assign them to members from the Members tab.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}
        {actionError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}

        {canManage ? (
          <AddRoleRow organizationId={organizationId} onCreated={refresh} />
        ) : null}

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Created</TableHead>
                {canManage && (
                  <TableHead className="text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell>
                      <Skeleton className="h-5 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Skeleton className="ml-auto h-8 w-8 rounded" />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 5 : 4} className="p-0">
                    <EmptyState
                      icon={Shield}
                      title="No custom roles"
                      description="Create roles to define fine-grained permissions."
                      className="border-0 rounded-none bg-transparent"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {role.description ?? "—"}
                    </TableCell>
                    <TableCell>
                      <PermissionBadges permissions={role.permissions ?? []} />
                    </TableCell>
                    <TableCell
                      title={role.createdAt}
                      className="text-sm text-muted-foreground"
                    >
                      {formatRelative(role.createdAt)}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditing(role)}
                          aria-label="Edit role"
                        >
                          <Pencil />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRole(role)}
                          loading={busy === `del-${role.id}`}
                          aria-label="Delete role"
                        >
                          <Trash2 className="text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {editing ? (
        <EditRoleDialog
          organizationId={organizationId}
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
    </Card>
  );
};

const AddRoleRow = ({
  organizationId,
  onCreated,
}: {
  organizationId: string;
  onCreated: () => void;
}) => {
  const client = useUmClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.createTenantRole({
      orgId: organizationId,
      name: name.trim(),
      description: description.trim() || undefined,
      permissions: parsePermissions(permissions),
    });
    setLoading(false);
    if (res.ok) {
      setName("");
      setDescription("");
      setPermissions("");
      onCreated();
    } else {
      setError(errorMessage(res.error, "Couldn't create role."));
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-end gap-3 rounded-lg border border-dashed bg-muted/30 p-4 sm:grid-cols-[1fr_1fr_1fr_auto]"
    >
      <Field label="Role name" htmlFor="role-name">
        <Input
          id="role-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. billing-admin"
        />
      </Field>
      <Field label="Description (optional)" htmlFor="role-desc">
        <Input
          id="role-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Can manage billing"
        />
      </Field>
      <Field label="Permissions (comma-separated)" htmlFor="role-perms">
        <Input
          id="role-perms"
          value={permissions}
          onChange={(e) => setPermissions(e.target.value)}
          placeholder="invoices:read, invoices:write"
          className="font-mono"
        />
      </Field>
      <Button type="submit" loading={loading} disabled={!name.trim()}>
        <Plus />
        Add role
      </Button>
      {error ? (
        <div className="sm:col-span-4">
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      ) : null}
    </form>
  );
};

const EditRoleDialog = ({
  organizationId,
  role,
  onClose,
  onSaved,
}: {
  organizationId: string;
  role: RoleRow;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [permissions, setPermissions] = useState(
    (role.permissions ?? []).join(", ")
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const res = await client.updateTenantRole({
      orgId: organizationId,
      roleId: role.id,
      name: name.trim(),
      description: description.trim(),
      permissions: parsePermissions(permissions),
    });
    setSaving(false);
    if (res.ok) {
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't update role."));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Edit role</DialogTitle>
            <DialogDescription>
              Rename <strong>{role.name}</strong> or change the permissions it
              grants. Changes apply to every member holding this role.
            </DialogDescription>
          </DialogHeader>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Field label="Role name" htmlFor="edit-role-name">
            <Input
              id="edit-role-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Description" htmlFor="edit-role-desc">
            <Input
              id="edit-role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field label="Permissions (comma-separated)" htmlFor="edit-role-perms">
            <Input
              id="edit-role-perms"
              value={permissions}
              onChange={(e) => setPermissions(e.target.value)}
              placeholder="invoices:read, invoices:write"
              className="font-mono"
            />
          </Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={saving} disabled={!name.trim()}>
              Save role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
